import axios from "axios";
import { CompanySettings, LoginCredentials, EmailRequest, EmailResponse, User, Company } from "@/types/auth";
import { store } from "@/store";
import { logout } from "@/store/slices/authSlice";

const AUTH_API_URL = import.meta.env.VITE_AUTH_API_URL;
const UTILS_API_URL = import.meta.env.VITE_UTILS_API_URL;
const CORE_API_URL = import.meta.env.VITE_CORE_API_URL;
const EXTENS_API_URL = import.meta.env.VITE_EXTENS_API_URL;

export const authAPI = {
  login: async (credentials: LoginCredentials) => {
    try {
      const response = await axios.post(
        `${AUTH_API_URL}/login`,
        {
          email: credentials.email,
          password: credentials.password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const responseData = response.data.body || response.data;

      if (responseData.success) {
        const { user, company, tokens } = responseData.data;

        let permissionsObject: Record<string, boolean> = {};

        if (Array.isArray(user.role.permissions)) {
          user.role.permissions.forEach((permission: string) => {
            permissionsObject[permission] = true;
          });
        } else if (typeof user.role.permissions === "object") {
          permissionsObject = user.role.permissions;
        }

        const adaptedUser: User = {
          id: user.id,
          email: user.email,
          name: `${user.first_name} ${user.last_name}`,
          first_name: user.first_name,
          last_name: user.last_name,
          phone: user.phone,
          avatar_url: user.avatar_url,
          is_owner: user.is_owner,
          role: {
            id: user.role.id,
            name: user.role.name,
            permissions: permissionsObject,
          },
        };

        const adaptedCompany: Company = {
          id: company.id,
          name: company.name,
          nombre_legal: company.nombre_legal,
          logo_url: company.logo_url,
          primary_color: company.primary_color,
          secondary_color: company.secondary_color,
          email: company.email,
          slug: company.slug,
          currency: company.currency,
          settings: company.settings || {},
        };

        localStorage.setItem("company", JSON.stringify(adaptedCompany));
        localStorage.setItem("user", JSON.stringify(adaptedUser));

        return {
          data: {
            user: adaptedUser,
            company: adaptedCompany,
            tokens: tokens,
          },
        };
      } else {
        throw new Error(responseData.message || "Error en el login");
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data?.body?.message || "Error en el servidor",
        );
      } else if (axios.isAxiosError(error) && error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  logout: async () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    localStorage.removeItem("company");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("refresh_token");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("company");
  },

  getProfile: async () => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const response = await axios.get(`${AUTH_API_URL}/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data.body || response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data?.body?.message || "Error al obtener perfil");
      }
      throw new Error("Error de conexión");
    }
  },

  changePassword: async (data: {
    user_id: string;
    current_password: string;
    new_password: string;
  }) => {
    try {
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");

      const response = await axios.post(
        `${AUTH_API_URL}/change-password`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );

      if (response.data.statusCode === 200 && response.data.body.success) {
        return {
          success: true,
          message:
            response.data.body.message ||
            "Contraseña actualizada correctamente",
        };
      } else {
        throw new Error(
          response.data.body?.message || "Error al cambiar la contraseña",
        );
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data?.body?.message || "Error en el servidor",
        );
      } else if (axios.isAxiosError(error) && error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  updateCompanySettings: async (data: {
    company_id: string;
    user_id: string;
    name?: string;
    email?: string;
    phone?: string;
    currency?: string;
    settings?: CompanySettings;
  }) => {
    try {
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");

      const response = await axios.put(`${AUTH_API_URL}/company/settings`, data, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const responseData = response.data.body || response.data;

      if (!responseData.success) {
        throw new Error(responseData.message || "Error al actualizar configuración");
      }

      return responseData;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.data?.body?.message) {
        throw new Error(error.response.data.body.message);
      }
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error("Error al actualizar configuración de empresa");
    }
  },

  resetPassword: async (data: {
    token: string;
    new_password: string;
    email?: string;
  }) => {
    try {
      const response = await axios.post(
        `${CORE_API_URL}/auth/reset-password`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const responseData = response.data.body || response.data;

      if (responseData.success) {
        return {
          success: true,
          message:
            responseData.message || "Contraseña restablecida correctamente",
        };
      } else {
        throw new Error(
          responseData.message || "Error al restablecer la contraseña",
        );
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.data?.body?.message)
        throw new Error(error.response.data.body.message);
      if (error instanceof Error) throw new Error(error.message);
      throw new Error("Error al restablecer la contraseña");
    }
  },
};

export const emailAPI = {
  sendWelcomeEmail: async (
    userId: string,
  ): Promise<{ data: EmailResponse }> => {
    await new Promise((resolve) => setTimeout(resolve, 2000));

    return {
      data: {
        success: true,
        message: "Email de bienvenida enviado correctamente",
        sentAt: new Date().toISOString(),
      },
    };
  },

  sendPasswordResetEmail: async (
    email: string,
  ): Promise<{ data: EmailResponse }> => {
    console.log(
      `📬 [emailAPI.sendPasswordResetEmail] 1. Iniciando envío para: ${email}`,
    );
    try {
      console.log(
        `📬 [emailAPI.sendPasswordResetEmail] 2. Llamando a: ${CORE_API_URL}/auth/forgot-password`,
      );
      const response = await axios.post(
        `${CORE_API_URL}/auth/forgot-password`,
        { email },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      console.log(
        "📬 [emailAPI.sendPasswordResetEmail] 3. Respuesta cruda del servidor:",
        response,
      );
      const responseData = response.data.body || response.data;

      if (responseData.success) {
        console.log(
          "✅ [emailAPI.sendPasswordResetEmail] 4. El servidor respondió con éxito.",
        );
        return {
          data: {
            success: true,
            message: responseData.message || "Instrucciones enviadas",
            sentAt: new Date().toISOString(),
          },
        };
      } else {
        console.warn(
          "⚠️ [emailAPI.sendPasswordResetEmail] 4. El servidor respondió con un error:",
          responseData.message,
        );
        throw new Error(
          responseData.message || "Error al solicitar recuperación",
        );
      }
    } catch (error: unknown) {
      console.error(
        "❌ [emailAPI.sendPasswordResetEmail] 5. Error en el bloque catch:",
        error,
      );
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data?.body?.message || "Error en el servidor",
        );
      } else if (axios.isAxiosError(error) && error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },
};

export const api = axios.create({
  baseURL: AUTH_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const modApi = axios.create({
  baseURL: CORE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const contactoStatusApi = axios.create({
  baseURL: UTILS_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const extensApi = axios.create({
  baseURL: EXTENS_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    const authEndpoints = ["/login"];

    const isAuthEndpoint = authEndpoints.some((endpoint) =>
      config.url?.endsWith(endpoint),
    );

    if (token && !isAuthEndpoint) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      response.data.statusCode === 200 &&
      response.data.body
    ) {
      response.data = response.data.body;
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      store.dispatch(logout());
      window.location.href = "/login";
    }

    if (error.response?.data?.body?.message) {
      error.message = error.response.data.body.message;
    }

    return Promise.reject(error);
  },
);

modApi.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

modApi.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      response.data.statusCode === 200 &&
      response.data.body
    ) {
      response.data = response.data.body.data || response.data.body;
    }

    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      store.dispatch(logout());
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

contactoStatusApi.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

contactoStatusApi.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      response.data.statusCode === 200 &&
      response.data.body
    ) {
      response.data = response.data.body.data || response.data.body;
    }

    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      store.dispatch(logout());
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

extensApi.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

extensApi.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      (response.data.statusCode === 200 || response.data.statusCode === 201) &&
      response.data.body
    ) {
      response.data = response.data.body.data || response.data.body;
    }

    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      store.dispatch(logout());
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);
